import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { mediaApi, getFileUrl, formatApiError, getEventFeedWsUrl } from "../lib/api";
import { toast } from "sonner";
import {
  Camera, Heart, ArrowLeft, Loader2,
  ImagePlus, X, Users, Image as ImageIcon, Aperture, Play, Volume2, VolumeX,
  ShieldAlert, AlertTriangle, Tv, TvIcon, EyeOff, Eye
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { UploadLimitBanner } from "../components/PaywallBanner";

// Thumb component that creates a blob URL from the File and revokes it on unmount
function SelectedFileThumb({ file, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const type = typeof file?.type === "string" ? file.type : "";
  const isImage = type.startsWith("image");

  useEffect(() => {
    let url = null;
    if (isImage && file) {
      try {
        url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } catch (_) {
        setPreviewUrl(null);
      }
    }
    return () => {
      if (url) {
        try { URL.revokeObjectURL(url); } catch (_) {}
      }
    };
  }, [file, isImage]);

  return (
    <div className="relative">
      <div className="w-16 h-16 bg-[#F5F5F4] border-2 border-[#E7E5E4] rounded-lg overflow-hidden">
        {isImage && previewUrl ? (
          <img src={previewUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Play className="w-6 h-6 text-[#78716C]" />
          </div>
        )}
      </div>
      <button
        onClick={onRemove}
        className="absolute -top-1 -right-1 w-5 h-5 bg-[#1C1917] text-white rounded-full flex items-center justify-center"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function VideoPlayer({ src }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="relative group">
      <video
        ref={videoRef}
        src={src}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        className="w-full object-cover rounded-t-xl cursor-pointer"
        onClick={togglePlay}
        data-testid="video-player"
      />
      {!playing && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/20"
          data-testid="video-play-btn"
        >
          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
            <Play className="w-6 h-6 text-[#1C1917] ml-0.5" fill="#1C1917" />
          </div>
        </button>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); setMuted(!muted); }}
        className="absolute bottom-2 right-2 w-8 h-8 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid="video-mute-btn"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
    </div>
  );
}

function PhotoCard({ item, guestId, fileUrlFn, isNew = false, isHost = false, onToggleTv }) {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(item.likes_count || 0);
  const [animating, setAnimating] = useState(false);
  // Optimistic local state for the host's TV toggle so the UI reacts
  // instantly before the server round-trip completes. Falls back to the
  // server value (default: true) for guest viewers.
  const [showOnTv, setShowOnTvLocal] = useState(item.show_on_tv !== false);
  const [togglingTv, setTogglingTv] = useState(false);
  const isVideo = item.type === "video" || item.content_type?.startsWith("video");

  // Sync when the parent updates likes_count (e.g., from a WS like_update
  // broadcast coming from another client).
  useEffect(() => {
    setLikes(item.likes_count || 0);
  }, [item.likes_count]);

  // Sync show_on_tv when the parent gets a fresh server value (e.g., WS
  // show_on_tv_update from another host device).
  useEffect(() => {
    setShowOnTvLocal(item.show_on_tv !== false);
  }, [item.show_on_tv]);

  const handleLike = async () => {
    try {
      const { data } = await mediaApi.like(item.media_id, guestId);
      setLiked(data.liked);
      setLikes(data.likes_count);
      if (data.liked) {
        setAnimating(true);
        setTimeout(() => setAnimating(false), 300);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleTv = async (e) => {
    e.stopPropagation();
    if (togglingTv) return;
    const next = !showOnTv;
    setShowOnTvLocal(next); // optimistic
    setTogglingTv(true);
    try {
      await mediaApi.setShowOnTv(item.media_id, next);
      onToggleTv?.(item.media_id, next);
      toast.success(next ? "Aparece na TV" : "Removida da TV");
    } catch (err) {
      // Roll back optimistic update on error.
      setShowOnTvLocal(!next);
      toast.error(formatApiError(err));
    } finally {
      setTogglingTv(false);
    }
  };

  return (
    <div
      className={`bg-white border-2 border-[#1C1917] shadow-[3px_3px_0px_#1C1917] rounded-xl overflow-visible break-inside-avoid mb-3 relative transition-shadow ${
        isNew ? "ring-4 ring-[#FF6B6B] ring-offset-2 ring-offset-[#FFFBF7]" : ""
      }`}
      data-testid={`photo-card-${item.media_id}`}
    >
      {isNew && (
        <>
          {/* Soft pulsing aura behind the card so the image stays bright */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -inset-1 rounded-xl bg-[#FF6B6B]/30 animate-pulse -z-10"
          />
          {/* "NOVA" ribbon that overflows the top-left corner so it is
              clearly visible independently of category/video chips. */}
          <span
            className="absolute -top-3 -left-2 z-20 bg-[#FF6B6B] text-white text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border-2 border-[#1C1917] shadow-[2px_2px_0px_#1C1917] animate-bounce flex items-center gap-1"
            data-testid="new-media-badge"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-white" />
            Nova
          </span>
        </>
      )}
      <div className="overflow-hidden rounded-t-xl relative">
        {isVideo ? (
          <VideoPlayer src={fileUrlFn(item.storage_path)} />
        ) : (
          <img
            src={fileUrlFn(item.storage_path)}
            alt={item.description || "Photo"}
            className="w-full object-cover"
            loading="lazy"
          />
        )}
        {item.categories?.length > 0 && (
          <div className="absolute top-2 left-2 flex flex-wrap gap-1">
            {item.categories.slice(0, 2).map((cat) => (
              <span key={cat} className="bg-[#8B5CF6]/90 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full backdrop-blur-sm">
                {cat}
              </span>
            ))}
          </div>
        )}
        {isVideo && (
          <span className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full backdrop-blur-sm">
            Video
          </span>
        )}
        {/* Show "Solo no album" badge for everyone (guests + host) when the
            media is hidden from the TV slideshow. The host gets an extra
            toggle button (top-right) to flip this state without visiting
            the album page. */}
        {!showOnTv && !isHost && (
          <span
            className={`absolute ${isVideo ? "top-9" : "top-2"} right-2 bg-[#1C1917]/80 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full backdrop-blur-sm flex items-center gap-1 border border-white/20`}
            data-testid={`no-tv-badge-${item.media_id}`}
            title="Esta midia nao aparece na TV da festa"
          >
            <span className="relative inline-flex w-3 h-3 items-center justify-center">
              <Tv className="w-3 h-3" strokeWidth={2.5} />
              <span
                aria-hidden="true"
                className="absolute inset-0 flex items-center justify-center"
              >
                <span className="block w-full h-[1.5px] bg-white rotate-45 rounded-full" />
              </span>
            </span>
            So no album
          </span>
        )}

        {/* Host-only toggle: hide/show this specific media on the TV. The
            button always renders for the host so they can curate the slideshow
            in real time. Click stops propagation so it doesn't open the photo. */}
        {isHost && (
          <button
            onClick={handleToggleTv}
            disabled={togglingTv}
            className={`absolute ${isVideo ? "top-9" : "top-2"} right-2 z-10 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase border-2 border-[#1C1917] shadow-[2px_2px_0px_#1C1917] transition-all hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] disabled:opacity-60 ${
              showOnTv
                ? "bg-white text-[#1C1917]"
                : "bg-[#1C1917] text-white"
            }`}
            data-testid={`host-toggle-tv-${item.media_id}`}
            title={showOnTv ? "Remover da TV" : "Mostrar na TV"}
          >
            {togglingTv ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : showOnTv ? (
              <>
                <Eye className="w-3 h-3" strokeWidth={2.8} /> Na TV
              </>
            ) : (
              <>
                <EyeOff className="w-3 h-3" strokeWidth={2.8} /> Oculta
              </>
            )}
          </button>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#78716C] font-bold uppercase tracking-wider">{item.nickname}</span>
          <button
            onClick={handleLike}
            className="flex items-center gap-1 group"
            data-testid={`like-btn-${item.media_id}`}
          >
            <Heart
              className={`w-5 h-5 transition-all ${liked ? "fill-[#FF6B6B] text-[#FF6B6B]" : "text-[#78716C] group-hover:text-[#FF6B6B]"} ${animating ? "scale-125" : "scale-100"}`}
              strokeWidth={2.5}
            />
            <span className="text-sm font-bold text-[#44403C]">{likes}</span>
          </button>
        </div>
        {item.description && (
          <p className="text-xs text-[#78716C] mt-1 line-clamp-2">{item.description}</p>
        )}
      </div>
    </div>
  );
}

export default function GuestFeed() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [media, setMedia] = useState([]);
  const [event, setEvent] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [freeLimit, setFreeLimit] = useState(50);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [blockedInfo, setBlockedInfo] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [newMediaIds, setNewMediaIds] = useState(() => new Set());
  const [showOnTv, setShowOnTv] = useState(true);
  // Per-file upload progress: { [fileKey]: { name, percent, status } }
  // status: "uploading" | "processing" | "done" | "error"
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const pollRef = useRef(null);
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);
  const wsAttemptRef = useRef(0);
  const latestTsRef = useRef(null);
  // Track IDs we've already processed in WS so duplicate messages don't
  // re-trigger side effects (toast, pulse highlight). Using a ref keeps
  // this out of React state so it doesn't participate in re-renders.
  const seenMediaIdsRef = useRef(new Set());

  const guest = JSON.parse(localStorage.getItem(`festa_guest_${eventId}`) || "null");
  const guestId = guest?.guest_id || guest?.device_id || "";
  const nickname = guest?.nickname || "anonymous";

  const loadFeed = useCallback(async (afterTs = null) => {
    try {
      const { data } = await mediaApi.feed(eventId, afterTs);
      if (afterTs && data.media.length > 0) {
        const newest = data.media[0];
        setMedia((prev) => {
          const existingIds = new Set(prev.map((m) => m.media_id));
          const newItems = data.media.filter((m) => !existingIds.has(m.media_id));
          return [...newItems, ...prev];
        });
        if (newest?.created_at) latestTsRef.current = newest.created_at;
      } else if (!afterTs) {
        setMedia(data.media);
        if (data.media.length > 0) latestTsRef.current = data.media[0].created_at;
      }
      setEvent(data.event);
      setIsHost(Boolean(data.is_host));
      setTotalCount(data.total_count || 0);
      setFreeLimit(data.free_limit || 30);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadFeed();

    // Realtime via WebSocket. We still keep a very slow polling fallback
    // (60s) to cover edge cases where the socket silently dies (e.g.,
    // intermediaries drop idle connections) or the browser doesn't support
    // WebSocket at all.
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      let ws;
      try {
        ws = new WebSocket(getEventFeedWsUrl(eventId));
      } catch (e) {
        console.warn("WS connect failed:", e);
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        wsAttemptRef.current = 0; // reset backoff on successful connect
        setWsConnected(true);
      };

      ws.onmessage = (ev) => {
        let msg;
        try {
          msg = JSON.parse(ev.data);
        } catch (_) {
          return;
        }
        if (!msg || typeof msg !== "object") return;
        if (msg.type === "new_media" && msg.media) {
          const item = msg.media;
          // Dedup using a ref so state updaters stay pure.
          if (seenMediaIdsRef.current.has(item.media_id)) return;
          seenMediaIdsRef.current.add(item.media_id);
          latestTsRef.current = item.created_at || latestTsRef.current;

          // PURE updater: just prepend if not already present.
          setMedia((prev) => {
            if (prev.some((m) => m.media_id === item.media_id)) return prev;
            return [item, ...prev];
          });
          setTotalCount((c) => c + 1);

          // Side effects live OUTSIDE the updater. Skip them for the
          // uploader's own media (they already got a confirmation toast).
          if (item.guest_id && item.guest_id !== guestId) {
            const who = item.nickname || "um convidado";
            toast(`Nova foto de ${who}!`, {
              icon: <Camera className="w-4 h-4 text-[#FF6B6B]" />,
              duration: 3000,
            });
            setNewMediaIds((prev) => {
              const next = new Set(prev);
              next.add(item.media_id);
              return next;
            });
            setTimeout(() => {
              setNewMediaIds((prev) => {
                if (!prev.has(item.media_id)) return prev;
                const next = new Set(prev);
                next.delete(item.media_id);
                return next;
              });
            }, 5000);
          }
        } else if (msg.type === "like_update" && msg.media_id) {
          setMedia((prev) =>
            prev.map((m) =>
              m.media_id === msg.media_id
                ? { ...m, likes_count: msg.likes_count }
                : m
            )
          );
        } else if (msg.type === "show_on_tv_update" && msg.media_id) {
          // Propagated when the host toggles a media's TV visibility from
          // any device. Keeps every viewer's badge in sync.
          setMedia((prev) =>
            prev.map((m) =>
              m.media_id === msg.media_id
                ? { ...m, show_on_tv: Boolean(msg.show_on_tv) }
                : m
            )
          );
        }
      };

      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null;
        setWsConnected(false);
        scheduleReconnect();
      };
      ws.onerror = () => {
        try { ws.close(); } catch (_) {}
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      const attempt = wsAttemptRef.current + 1;
      wsAttemptRef.current = attempt;
      // Exponential backoff capped at 30s: 1s, 2s, 4s, 8s, 16s, 30s.
      const delay = Math.min(30000, 1000 * 2 ** Math.min(attempt - 1, 5));
      if (wsReconnectRef.current) clearTimeout(wsReconnectRef.current);
      wsReconnectRef.current = setTimeout(connect, delay);
    };

    connect();

    // Fallback safety poll - much slower now since WS handles realtime.
    pollRef.current = setInterval(() => {
      loadFeed(latestTsRef.current);
    }, 60000);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (wsReconnectRef.current) clearTimeout(wsReconnectRef.current);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        try { ws.close(); } catch (_) {}
      }
    };
  }, [eventId, loadFeed]);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    const blocked = [];
    const duplicates = [];
    let successCount = 0;
    let fatalError = null;
    // Seed progress map with all files at 0%
    setUploadProgress(
      Object.fromEntries(
        selectedFiles.map((f, i) => [
          `${i}-${f.name}`,
          { name: f.name, percent: 0, status: "uploading" },
        ])
      )
    );
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const key = `${i}-${file.name}`;
        const formData = new FormData();
        formData.append("file", file);
        formData.append("guest_id", guestId);
        formData.append("nickname", nickname);
        formData.append("show_on_tv", showOnTv ? "true" : "false");
        try {
          const { data } = await mediaApi.upload(
            eventId,
            formData,
            (e) => {
              if (!e?.total) return;
              const percent = Math.round((e.loaded / e.total) * 100);
              setUploadProgress((prev) => ({
                ...prev,
                [key]: {
                  ...prev[key],
                  percent,
                  status: percent >= 100 ? "processing" : "uploading",
                },
              }));
            }
          );
          if (data.is_safe === false) {
            blocked.push({
              filename: file.name,
              reason: data.block_reason || "Conteudo impróprio detectado",
            });
            setUploadProgress((prev) => ({
              ...prev,
              [key]: { ...prev[key], percent: 100, status: "error" },
            }));
          } else {
            successCount += 1;
            setUploadProgress((prev) => ({
              ...prev,
              [key]: { ...prev[key], percent: 100, status: "done" },
            }));
          }
        } catch (err) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail;
          if (status === 409) {
            duplicates.push({ filename: file.name, reason: detail || "Essa foto ja foi enviada." });
            setUploadProgress((prev) => ({
              ...prev,
              [key]: { ...prev[key], percent: 100, status: "error" },
            }));
          } else if (detail && typeof detail === "string" && detail.includes("Limite")) {
            fatalError = detail;
            break;
          } else {
            throw err;
          }
        }
      }
      setSelectedFiles([]);
      setUploadOpen(false);
      setUploadProgress({});
      loadFeed();
      if (fatalError) {
        setBlockedInfo([{ filename: "", reason: fatalError }]);
      } else if (blocked.length > 0) {
        setBlockedInfo(blocked);
      } else if (duplicates.length > 0 && successCount === 0) {
        toast.error(duplicates[0].reason, { duration: 4000 });
      } else if (duplicates.length > 0) {
        toast.success(`${successCount} enviada(s). ${duplicates.length} ja existia(m).`);
      } else {
        toast.success("Foto enviada!");
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Erro ao enviar. Tente de novo.");
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  // Direct upload (no preview) - used for camera capture to avoid iOS Safari
  // crashing when rendering a preview of a large camera photo right after the
  // Camera app closes. Uploads immediately and shows a toast for feedback.
  const uploadFilesDirectly = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const blocked = [];
    const duplicates = [];
    let successCount = 0;
    let fatalError = null;
    // Open upload dialog to surface progress UI for the camera/inline path
    setUploadOpen(true);
    setUploadProgress(
      Object.fromEntries(
        Array.from(files).map((f, i) => [
          `${i}-${f.name}`,
          { name: f.name, percent: 0, status: "uploading" },
        ])
      )
    );
    const toastId = toast.loading(
      files.length === 1 ? "Enviando foto..." : `Enviando ${files.length} fotos...`
    );
    try {
      const arr = Array.from(files);
      for (let i = 0; i < arr.length; i++) {
        const file = arr[i];
        const key = `${i}-${file.name}`;
        const formData = new FormData();
        formData.append("file", file);
        formData.append("guest_id", guestId);
        formData.append("nickname", nickname);
        formData.append("show_on_tv", showOnTv ? "true" : "false");
        try {
          const { data } = await mediaApi.upload(
            eventId,
            formData,
            (e) => {
              if (!e?.total) return;
              const percent = Math.round((e.loaded / e.total) * 100);
              setUploadProgress((prev) => ({
                ...prev,
                [key]: {
                  ...prev[key],
                  percent,
                  status: percent >= 100 ? "processing" : "uploading",
                },
              }));
            }
          );
          if (data?.is_safe === false) {
            blocked.push({
              filename: file.name,
              reason: data.block_reason || "Conteudo impróprio detectado",
            });
            setUploadProgress((prev) => ({
              ...prev,
              [key]: { ...prev[key], percent: 100, status: "error" },
            }));
          } else {
            successCount += 1;
            setUploadProgress((prev) => ({
              ...prev,
              [key]: { ...prev[key], percent: 100, status: "done" },
            }));
          }
        } catch (err) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail;
          if (status === 409) {
            duplicates.push({ filename: file.name, reason: detail || "Essa foto ja foi enviada." });
            setUploadProgress((prev) => ({
              ...prev,
              [key]: { ...prev[key], percent: 100, status: "error" },
            }));
          } else if (detail && typeof detail === "string" && detail.includes("Limite")) {
            fatalError = detail;
            break;
          } else {
            throw err;
          }
        }
      }
      toast.dismiss(toastId);
      setUploadOpen(false);
      setUploadProgress({});
      loadFeed();
      if (fatalError) {
        setBlockedInfo([{ filename: "", reason: fatalError }]);
      } else if (blocked.length > 0) {
        setBlockedInfo(blocked);
      } else if (duplicates.length > 0 && successCount === 0) {
        toast.error(duplicates[0].reason, { duration: 4000 });
      } else if (duplicates.length > 0) {
        toast.success(`${successCount} enviada(s). ${duplicates.length} ja existia(m).`);
      } else {
        toast.success("Foto enviada!");
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(formatApiError(err) || "Erro ao enviar. Tente de novo.");
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF7] pb-20" data-testid="guest-feed-page">
      {/* Header */}
      <header className="sticky top-0 w-full bg-[#FFFBF7]/80 backdrop-blur-xl border-b-2 border-[#1C1917] z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => {
                // If we got here from somewhere within the app (host's
                // EventPage, GuestEntry, etc.), go back. Otherwise (direct
                // open from a QR / shared link), fall back to GuestEntry.
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate(`/join/${eventId}`);
                }
              }}
              className="bg-transparent border-0 p-0 m-0 cursor-pointer"
              data-testid="feed-back-btn"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
            </button>
            <span className="font-black text-lg tracking-tight text-[#1C1917] truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {event?.name || "Feed"}
            </span>
            {wsConnected && (
              <span
                className="shrink-0 inline-flex items-center gap-1 bg-[#FF6B6B] text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full border-2 border-[#1C1917] shadow-[1px_1px_0px_#1C1917]"
                data-testid="live-badge"
                title="Feed ao vivo"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-80" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                </span>
                Ao vivo
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-[#78716C]">
            <Users className="w-4 h-4" strokeWidth={2.5} />
            <span>{event?.guest_count || 0}</span>
            <ImageIcon className="w-4 h-4 ml-2" strokeWidth={2.5} />
            <span>{event?.media_count || 0}</span>
          </div>
        </div>
      </header>

      {/* Feed */}
      <div className="max-w-2xl mx-auto">
        {/* Feed */}
        <div className="px-3 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" />
            </div>
          ) : media.length === 0 ? (
            <div className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-12 text-center mt-8">
              <div className="w-16 h-16 bg-[#FEE2E2] border-2 border-[#1C1917] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-[2px_2px_0px_#1C1917]">
                <Camera className="w-8 h-8 text-[#FF6B6B]" strokeWidth={2.5} />
              </div>
              <h3 className="font-bold text-[#1C1917] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Nenhuma foto ainda
              </h3>
              <p className="text-sm text-[#78716C]">Seja o primeiro a enviar uma foto!</p>
            </div>
          ) : (
            <div className="columns-2 gap-3">
              {media.map((item) => (
                <PhotoCard
                  key={item.media_id}
                  item={item}
                  guestId={guestId}
                  fileUrlFn={getFileUrl}
                  isNew={newMediaIds.has(item.media_id)}
                  isHost={isHost}
                  onToggleTv={(id, next) =>
                    setMedia((prev) =>
                      prev.map((m) => (m.media_id === id ? { ...m, show_on_tv: next } : m))
                    )
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hidden camera input - ALWAYS mounted at root level (outside Dialog)
          so the iPhone native camera can re-attach correctly on return.
          Uploads directly, no preview rendering (prevents iOS Safari memory
          crash when rendering big camera photos as <img> previews).       */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = "";
          if (files.length === 0) return;
          setUploadOpen(false);
          uploadFilesDirectly(files);
        }}
        data-testid="camera-input"
      />

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Enviar Fotos
            </DialogTitle>
          </DialogHeader>
          {/* Content rules hint - sets expectations before upload to reduce
              frustration after AI moderation rejects bad content. */}
          <div
            className="flex items-start gap-2 bg-[#FEF3C7] border-2 border-[#1C1917] rounded-lg p-3 shadow-[2px_2px_0px_#1C1917]"
            data-testid="upload-rules-hint"
          >
            <AlertTriangle className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" strokeWidth={2.5} />
            <p className="text-[11px] text-[#78716C] leading-snug">
              <span className="font-bold text-[#1C1917]">Envie fotos com pessoas ou animais.</span>
              {" "}Documentos, prints e imagens so com texto nao sao permitidos.
            </p>
          </div>

          {/* Show on TV toggle: lets the guest opt OUT of having this photo
              appear on the live slideshow, while still contributing to the
              album. Default: ON. */}
          <label
            className="flex items-center gap-3 bg-white border-2 border-[#1C1917] rounded-lg p-3 shadow-[2px_2px_0px_#1C1917] cursor-pointer select-none"
            data-testid="show-on-tv-toggle"
          >
            <div className="w-8 h-8 bg-[#1C1917] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shrink-0 shadow-[1px_1px_0px_#78716C]">
              <Tv className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-bold text-[#1C1917] leading-tight">
                Mostrar na TV ao vivo
              </p>
              <p className="text-[10px] text-[#78716C] leading-tight">
                {showOnTv
                  ? "Sua foto vai aparecer na TV da festa."
                  : "Sua foto vai so para o album (nao aparece na TV)."}
              </p>
            </div>
            <input
              type="checkbox"
              checked={showOnTv}
              onChange={(e) => setShowOnTv(e.target.checked)}
              className="sr-only peer"
              data-testid="show-on-tv-checkbox"
            />
            <span
              className="relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-[#1C1917] bg-[#E7E5E4] peer-checked:bg-[#FF6B6B] transition-colors shadow-[1px_1px_0px_#1C1917]"
              aria-hidden="true"
            >
              <span
                className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white border-2 border-[#1C1917] transition-transform ${
                  showOnTv ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </span>
          </label>

          {/* Upload progress panel - shown while files are being sent so the
              guest sees a real percentage instead of a vague spinner. */}
          {uploading && Object.keys(uploadProgress).length > 0 && (
            <div
              className="space-y-2 bg-white border-2 border-[#1C1917] rounded-lg p-3 shadow-[2px_2px_0px_#1C1917]"
              data-testid="upload-progress-panel"
            >
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#1C1917]">
                Enviando {Object.keys(uploadProgress).length}{" "}
                {Object.keys(uploadProgress).length === 1 ? "arquivo" : "arquivos"}
              </p>
              {Object.entries(uploadProgress).map(([key, info]) => {
                const colorClass =
                  info.status === "error"
                    ? "bg-[#EF4444]"
                    : info.status === "done"
                    ? "bg-[#22C55E]"
                    : "bg-[#FF6B6B]";
                const label =
                  info.status === "processing"
                    ? "Processando com IA..."
                    : info.status === "done"
                    ? "Enviada"
                    : info.status === "error"
                    ? "Erro"
                    : `${info.percent}%`;
                return (
                  <div key={key} data-testid={`upload-progress-row-${key}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[11px] text-[#1C1917] truncate flex-1" title={info.name}>
                        {info.name}
                      </span>
                      <span
                        className={`text-[10px] font-bold tabular-nums shrink-0 ${
                          info.status === "error"
                            ? "text-[#EF4444]"
                            : info.status === "done"
                            ? "text-[#22C55E]"
                            : "text-[#78716C]"
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-[#E7E5E4] rounded-full overflow-hidden border border-[#1C1917]">
                      <div
                        className={`h-full ${colorClass} transition-[width] duration-200 ${
                          info.status === "processing" ? "animate-pulse" : ""
                        }`}
                        style={{ width: `${info.percent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="space-y-4">
            {/* Camera + Gallery buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  // Trigger camera click synchronously first (iOS Safari
                  // requires a user gesture click chain - no setTimeout).
                  // Dialog is closed IMMEDIATELY after so that when iOS
                  // returns from the native camera app, there's no stale
                  // Dialog state / Radix focus trap to restore.
                  try { cameraInputRef.current?.click(); } catch (_) {}
                  setUploadOpen(false);
                }}
                className="border-2 border-[#1C1917] rounded-xl p-5 text-center hover:bg-[#FEE2E2]/30 transition-colors bg-white shadow-[3px_3px_0px_#1C1917] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#1C1917]"
                data-testid="camera-capture-btn"
              >
                <Aperture className="w-10 h-10 text-[#FF6B6B] mx-auto mb-2" strokeWidth={2.5} />
                <p className="text-sm font-bold text-[#1C1917]">Tirar Foto</p>
                <p className="text-[10px] text-[#78716C]">Usar camera</p>
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-[#1C1917] rounded-xl p-5 text-center hover:bg-[#FEE2E2]/30 transition-colors bg-white shadow-[3px_3px_0px_#1C1917] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#1C1917]"
                data-testid="gallery-select-btn"
              >
                <ImagePlus className="w-10 h-10 text-[#8B5CF6] mx-auto mb-2" strokeWidth={2.5} />
                <p className="text-sm font-bold text-[#1C1917]">Galeria</p>
                <p className="text-[10px] text-[#78716C]">Escolher fotos</p>
              </button>
            </div>

            {/* Hidden gallery input (multi-select from photo library) */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                e.target.value = "";
                if (files.length > 0) setSelectedFiles((prev) => [...prev, ...files]);
              }}
              data-testid="file-input"
            />

            {selectedFiles.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-bold text-[#1C1917]">{selectedFiles.length} arquivo(s) selecionado(s)</p>
                <div className="flex flex-wrap gap-2">
                  {selectedFiles.map((f, i) => (
                    <SelectedFileThumb
                      key={i}
                      file={f}
                      onRemove={() => setSelectedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    />
                  ))}
                </div>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all"
                  data-testid="upload-submit-btn"
                >
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enviar"}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Blocked Content Popup */}
      <Dialog open={blockedInfo !== null} onOpenChange={() => setBlockedInfo(null)}>
        <DialogContent className="border-2 border-red-500 shadow-[6px_6px_0px_#EF4444] rounded-xl max-w-sm mx-auto" data-testid="blocked-content-dialog">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 border-2 border-red-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-[2px_2px_0px_#EF4444]">
              <ShieldAlert className="w-8 h-8 text-red-600" strokeWidth={2.5} />
            </div>
            <h2 className="text-xl font-black text-[#1C1917] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Conteudo Bloqueado
            </h2>
            <p className="text-sm text-[#78716C] mb-4">
              Nossa IA detectou conteudo impróprio. A foto nao sera exibida no feed.
            </p>
            <div className="space-y-2 mb-5">
              {blockedInfo?.map((item, i) => (
                <div
                  key={i}
                  className="bg-red-50 border-2 border-red-200 rounded-xl p-3 flex items-start gap-3 text-left"
                  data-testid={`blocked-item-${i}`}
                >
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" strokeWidth={2.5} />
                  <div>
                    {item.filename && <p className="text-xs font-bold text-[#1C1917] truncate">{item.filename}</p>}
                    <p className="text-sm text-red-700 font-medium">{item.reason}</p>
                  </div>
                </div>
              ))}
            </div>
            <Button
              onClick={() => setBlockedInfo(null)}
              className="w-full bg-[#1C1917] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#78716C] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#78716C] transition-all"
              data-testid="blocked-dismiss-btn"
            >
              Entendi
            </Button>
            <p className="text-[10px] text-[#78716C] mt-3 uppercase tracking-wider font-bold">
              Moderacao automatica por Festa.IA
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bottom Nav - guests only see the central Upload button.
          Albums is host-only (premium). The Feed button was removed because
          guests are already on the feed page - it was redundant (and was
          being auto-translated to "alimentar" by Chrome, confusing users). */}
      <nav className="fixed bottom-0 w-full bg-white border-t-2 border-[#1C1917] z-50" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }} data-testid="bottom-nav">
        <div className="max-w-lg mx-auto flex justify-center items-center px-6 py-2">
          <button
            onClick={() => setUploadOpen(true)}
            className="w-14 h-14 -mt-6 bg-[#FF6B6B] border-2 border-[#1C1917] rounded-full flex items-center justify-center shadow-[3px_3px_0px_#1C1917] hover:translate-y-[2px] hover:shadow-[1px_1px_0px_#1C1917] transition-all"
            data-testid="nav-upload"
            aria-label="Enviar foto"
          >
            <Camera className="w-7 h-7 text-white" strokeWidth={2.5} />
          </button>
        </div>
      </nav>
    </div>
  );
}
